'use client'; // Required for useUser hook and state

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { useUser } from '@auth0/nextjs-auth0/client';
import { usePathname, useRouter } from 'next/navigation'; // Import hooks

const Header = () => {
  const { user, error, isLoading } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShowcaseOpen, setIsShowcaseOpen] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false); // New state for About Us dropdown
  const showcaseRef = useRef<HTMLDivElement>(null);
  const aboutUsRef = useRef<HTMLDivElement>(null); // New ref for About Us dropdown
  const [clientSlug, setClientSlug] = useState<string | null>(null);
  const router = useRouter(); // Get router instance
  const pathname = usePathname(); // Get current path

  // Effect to fetch client slug AND perform redirect
  useEffect(() => {
    if (!isLoading && user) {
      fetch('/api/auth/session-details')
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to fetch session details: ${res.statusText}`);
          return res.json();
        })
        .then((data) => {
          if (data && data.clientId) {
            const fetchedClientId = data.clientId;
            setClientSlug(fetchedClientId);
            if (pathname === '/' && !isLoading && user) {
              router.push(`/client_portal/${fetchedClientId}`); // Reverted to client-side nav
            }
          } else {
            setClientSlug(null);
          }
        })
        .catch((err) => {
          console.error('Error fetching client slug:', err);
          setClientSlug(null);
        });
    }
    if (!isLoading && !user) {
      setClientSlug(null);
    }
  }, [user, pathname, router, isLoading]); // Removed clientSlug from dependencies to prevent unnecessary fetches

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showcaseRef.current && !showcaseRef.current.contains(event.target as Node)) {
        setIsShowcaseOpen(false);
      }
      if (aboutUsRef.current && !aboutUsRef.current.contains(event.target as Node)) {
        setIsAboutUsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showcaseRef, aboutUsRef]);

  if (isLoading) return <div className="h-22 bg-white"></div>; // Placeholder for height
  if (error && user) // Only show error if there's a user
    return (
      <div className="h-22 bg-white text-red-600 p-4">Error loading user: {error.message}</div>
    );

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-22">
        <div className="flex-shrink-0">
          <Link href="/">
            <Image
              src="/images/DJLogo2025.svg"
              alt="Data Jukebox Logo"
              width={161}
              height={64}
              priority
              placeholder="blur"
              blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYxIiBoZWlnaHQ9IjY0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxNjEiIGhlaWdodD0iNjQiIGZpbGw9IiM1OWI5YzEiLz48L3N2Zz4="
            />
          </Link>
        </div>

        {/* Desktop Navigation Links */}
        <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
          {/* Home button only for not logged in users */}
          {!user && (
            <Link href="/" className="text-gray-600 hover:text-[#59b9c1] font-medium" prefetch>
              Home
            </Link>
          )}

          {/* Conditionally show general links ONLY if user is NOT logged in */}
          {!user && (
            <>
              {/* Showcase Dropdown */}
              <div className="relative" ref={showcaseRef}>
                <button
                  type="button"
                  onClick={() => setIsShowcaseOpen(!isShowcaseOpen)}
                  className="text-gray-600 hover:text-[#59b9c1] font-medium flex items-center focus:outline-none"
                  aria-haspopup="true"
                  aria-expanded={isShowcaseOpen ? "true" : "false"}
                >
                  Showcase
                  <svg
                    className={`w-4 h-4 ml-1 transition-transform duration-200 ${isShowcaseOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>
                {/* Dropdown Menu */}
                {isShowcaseOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 py-1 ring-1 ring-black ring-opacity-5">
                    <Link
                      href="/products"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#59b9c1]"
                      onClick={() => setIsShowcaseOpen(false)}
                      prefetch
                    >
                      Products
                    </Link>
                    <Link
                      href="/case-studies"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#59b9c1]"
                      onClick={() => setIsShowcaseOpen(false)}
                      prefetch
                    >
                      Case Studies
                    </Link>
                  </div>
                )}
              </div>
              {/* About Us Dropdown */}
              <div className="relative" ref={aboutUsRef}>
                <button
                  type="button"
                  onClick={() => setIsAboutUsOpen(!isAboutUsOpen)}
                  className="text-gray-600 hover:text-[#59b9c1] font-medium flex items-center focus:outline-none"
                  aria-haspopup="true"
                  aria-expanded={isAboutUsOpen ? "true" : "false"}
                >
                  About Us
                  <svg
                    className={`w-4 h-4 ml-1 transition-transform duration-200 ${isAboutUsOpen ? 'transform rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </button>
                {/* Dropdown Menu */}
                {isAboutUsOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 py-1 ring-1 ring-black ring-opacity-5">
                    <Link
                      href="/about"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#59b9c1]"
                      onClick={() => setIsAboutUsOpen(false)}
                      prefetch
                    >
                      About Us
                    </Link>
                    <Link
                      href="/client_info"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#59b9c1]"
                      onClick={() => setIsAboutUsOpen(false)}
                      prefetch
                    >
                      Our Clients
                    </Link>
                    <Link
                      href="/testimonials"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-[#59b9c1]"
                      onClick={() => setIsAboutUsOpen(false)}
                      prefetch
                    >
                      Testimonials
                    </Link>
                  </div>
                )}
              </div>
              <Link
                href="/benchmark"
                className="text-gray-600 hover:text-[#59b9c1] font-medium"
                prefetch
              >
                Benchmark
              </Link>
              <Link
                href="/blog"
                className="text-gray-600 hover:text-[#59b9c1] font-medium"
                prefetch
              >
                Blog
              </Link>
              <Link
                href="/contact"
                className="text-gray-600 hover:text-[#59b9c1] font-medium"
                prefetch
              >
                Contact
              </Link>
            </>
          )}

          {/* Conditional Log In / Log Out */}
          {user ? (
            <a href="/api/auth/logout" className="text-gray-600 hover:text-[#59b9c1] font-medium">
              Log Out ({user.name || user.email})
            </a>
          ) : (
            <a href="/api/auth/login" className="text-gray-600 hover:text-[#59b9c1] font-medium">
              Log In
            </a>
          )}
        </div>

        <div className="md:hidden flex items-center">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-[#59b9c1] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#59b9c1]"
            aria-controls="mobile-menu"
            aria-expanded={isMobileMenuOpen ? "true" : "false"}
          >
            <span className="sr-only">Open main menu</span>
            {isMobileMenuOpen ? (
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                className="block h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Flyout*/}
      <div
        className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden absolute top-full left-0 w-full bg-white shadow-lg z-40`}
        id="mobile-menu"
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          {/* Show different links based on login state */}
          {!user ? (
            <>
              <Link
                href="/"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              {/* Showcase Section for Mobile */}
              <div className="px-3 py-2 font-medium text-gray-500">Showcase</div>
              <Link
                href="/products"
                className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Products
              </Link>
              <Link
                href="/case-studies"
                className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Case Studies
              </Link>
              {/* About Us Section for Mobile */}
              <div className="px-3 py-2 font-medium text-gray-500">About Us</div>
              <Link
                href="/about"
                className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About Us
              </Link>
              <Link
                href="/client_info"
                className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Our Clients
              </Link>
              <Link
                href="/testimonials"
                className="block pl-6 pr-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Testimonials
              </Link>
              {/* Standalone Links */}
              <Link
                href="/benchmark"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Benchmark
              </Link>
              <Link
                href="/blog"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Blog
              </Link>
              <Link
                href="/contact"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact
              </Link>
            </>
          ) : null}
        </div>

        {/* Conditional Mobile Log In / Log Out (Simplified) */}
        <div className="pt-4 pb-3 border-t border-gray-200">
          <div className="px-2 space-y-1">
            {user ? (
              <a
                href="/api/auth/logout"
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Log Out
              </a>
            ) : (
              <a
                href="/api/auth/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#59b9c1] hover:bg-gray-50"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Log In
              </a>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
